import type { AxiosInstance } from 'axios'
import type { IFactoryFunctionRequest } from '@bc-library/interface'
import { EMethod } from '@bc-library/interface'
import { API_MAIN_VERSION, BASE_URL } from '@/config/constantes'
import useAxios from '@/services/api/useAxios'

const resource = 'salesWorkflows'

export type SalesWorkflowTrigger = {
  type: 'stage_enter' | 'deal_created' | 'inactivity_days'
  stage?: number | null
  days?: number | null
}

export type SalesWorkflowActionType =
  | 'create_task'
  | 'create_note'
  | 'notify'
  | 'update_field'
  | 'webhook'

export type SalesWorkflowAction = {
  type: SalesWorkflowActionType
  subject?: string
  body?: string
  dueInDays?: number | null
  assignee?: 'owner' | 'author' | 'fixed'
  fixedAssigneeId?: string | null
  entity?: 'engagement' | 'company' | null
  field?: string | null
  value?: unknown
  url?: string
  method?: 'POST' | 'GET'
  payloadTemplate?: string | null
}

export type SalesWorkflowConditions = {
  minProbability?: number | null
  maxProbability?: number | null
  minLeadPrice?: number | null
  maxLeadPrice?: number | null
  icpScoreMin?: number | null
  icpFitMin?: 'weak' | 'medium' | 'strong' | null
}

export type SalesWorkflowStep = {
  delayDays?: number
  conditions?: SalesWorkflowConditions
  actions?: SalesWorkflowAction[]
}

export type SalesWorkflowPayload = {
  name: string
  description?: string
  isActive?: boolean
  trigger: SalesWorkflowTrigger
  conditions?: SalesWorkflowConditions
  actions?: SalesWorkflowAction[]
  steps?: SalesWorkflowStep[]
}

const list = (axios: IFactoryFunctionRequest<any, any>) => (query: Record<string, unknown> = {}) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2`,
    query,
  })
}

const getById = (axios: IFactoryFunctionRequest<any, any>) => (id: string) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${id}`,
  })
}

const create = (axios: IFactoryFunctionRequest<any, any>) => (data: SalesWorkflowPayload) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2`,
    data,
  })
}

const update = (axios: IFactoryFunctionRequest<any, any>) => (id: string, data: Partial<SalesWorkflowPayload>) => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${id}`,
    data,
  })
}

const softDelete = (axios: IFactoryFunctionRequest<any, any>) => (id: string) => {
  return axios({
    method: EMethod.delete,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${id}`,
  })
}

const runInactivity = (axios: IFactoryFunctionRequest<any, any>) => (data: { dryRun?: boolean } = {}) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/run-inactivity`,
    data,
  })
}

const processSequenceRuns = (axios: IFactoryFunctionRequest<any, any>) => (data: { limit?: number } = {}) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/process-sequence-runs`,
    data,
  })
}

export default (axios: AxiosInstance) => ({
  list: list(useAxios(axios)),
  getById: getById(useAxios(axios)),
  create: create(useAxios(axios)),
  update: update(useAxios(axios)),
  softDelete: softDelete(useAxios(axios)),
  runInactivity: runInactivity(useAxios(axios)),
  processSequenceRuns: processSequenceRuns(useAxios(axios)),
})
